import React from "react";
import type Highcharts from "highcharts";
import { color } from "@ui";
import Color from "color";
import { GROUP_LABEL_CLASS, GROUP_PLOTLINE_COLOR } from "@ui/Highcharts/ui/Wrapper";
import useObserverCallback from "@ui/lib/hooks/useObserverCallback";
import type { Group } from "../../../../api/useGroups";
import type { Period } from "@ui/Highcharts/hooks/usePeriod";
import { zIndexChart } from "@ui/Highcharts/BetterHighcharts";

const MAX_GROUPS_IN_PERIOD = 10;
const MAX_GROUPS = 50;
const MIN_GROUP_SIZE = 35;

function intersectPeriod(group: Group, period: Period) {
  return period.from < (group.nextStartDate ?? group.endDate) && group.startDate < period.to;
}

const startBeforeAxisLabelId = (chart: Highcharts.Chart) => `${chart.name}-group-label-before-axis`;
const groupStartPlotLineId = (group: Group) => `group-${group.startDate}-start`;
const groupEndPlotLineId = (group: Group) => `group-${group.endDate}-end`;
const groupEndPlotBandId = (group: Group) => `group-${group.endDate}-end`;

function renderGroup(
  chart: Highcharts.Chart,
  group: Group,
  setPeriod: (p: Period) => void,
  navigator: boolean,
) {
  const axisIndex = navigator ? 1 : 0;
  const xAxis = chart.xAxis[axisIndex];
  if (!xAxis) throw new Error(`Missing xAxis ${axisIndex}`);
  const { min } = xAxis.getExtremes();
  const startBeforeAxis = !navigator && group.startDate <= min;

  const startPlotLineValue = startBeforeAxis ? min : group.startDate;
  const groupWidth =
    xAxis.toPixels(group.nextStartDate ?? group.endDate, true) -
    xAxis.toPixels(startPlotLineValue, true);
  const isLargeEnough = groupWidth > MIN_GROUP_SIZE;
  const text = isLargeEnough ? (group.title ?? "") : "";
  let plotLine: Highcharts.PlotLineOrBand | undefined;
  let plotLineLabelElement: SVGElement | HTMLElement | undefined;

  if (startBeforeAxis) {
    removeGroupBeforeAxisLabel(chart);
    const el = chart.container.appendChild(document.createElement("div"));
    el.innerHTML = text;
    el.id = `${startBeforeAxisLabelId(chart)}`;
    el.className = `${GROUP_LABEL_CLASS} startBefore`;
    el.style.padding = isLargeEnough ? "4px 8px 4px 8px" : "0px 0px";
    el.style.maxWidth = `${groupWidth}px`;
    el.style.position = "absolute";
    el.style.left = `${chart.plotLeft}px`;
    el.style.top = `${chart.plotTop - 23}px`;
    plotLineLabelElement = el;
  } else {
    plotLine = xAxis.addPlotLine({
      id: groupStartPlotLineId(group),
      color: GROUP_PLOTLINE_COLOR,
      width: startBeforeAxis ? 0 : 2,
      value: startPlotLineValue,
      label: navigator
        ? undefined
        : {
            className: GROUP_LABEL_CLASS,
            text,
            style: {
              fontSize: "12px",
              padding: isLargeEnough ? "4px 10px 4px 8px" : "0px 0px",
              width: groupWidth + 2,
              textOverflow: "ellipsis",
            },
            rotation: 0,
            x: -1,
            y: -12,
            useHTML: true,
          },
    });
    plotLineLabelElement = plotLine?.label?.element;
  }

  if (!navigator && plotLineLabelElement) {
    plotLineLabelElement.addEventListener("click", () => {
      setPeriod({ from: group.startDate, to: group.endDate });
      xAxis.removePlotBand("groupOver");
    });
    plotLineLabelElement.addEventListener("mouseenter", () => {
      plotLine?.svgElem.attr({ stroke: color.lightNitrogen500_Main });
      xAxis.addPlotBand({
        id: "groupOver",
        color: new Color(color.raw.lightNitrogen500_Main).alpha(0.1).toString(),
        from: group.startDate,
        to: group.endDate,
      });
    });
    plotLineLabelElement.addEventListener("mouseleave", () => {
      plotLine?.svgElem.attr({ stroke: GROUP_PLOTLINE_COLOR });
      xAxis.removePlotBand("groupOver");
    });
  }

  if (group.endedByNull) {
    xAxis.addPlotLine({
      id: groupEndPlotLineId(group),
      color: GROUP_PLOTLINE_COLOR,
      width: 2,
      value: group.endDate,
      label: navigator
        ? undefined
        : {
            className: `${GROUP_LABEL_CLASS} end`,
            text: " ",
            style: {
              fontSize: "12px",
              padding: "4px 0px",
            },
            rotation: 0,
            x: -1,
            y: -11,
            useHTML: true,
          },
    });
  }

  if (group.nextStartDate !== null) {
    xAxis.addPlotBand({
      id: groupEndPlotBandId(group),
      from: group.endDate,
      to: group.nextStartDate,
      color: color.interGroupArea,
      zIndex: zIndexChart.plotBands,
    });
  }
}

function destroyGroup(chart: Highcharts.Chart, group: Group, navigator: boolean) {
  const axisIndex = navigator ? 1 : 0;
  const xAxis = chart.xAxis[axisIndex];
  if (!xAxis) throw new Error(`Missing xAxis ${axisIndex}`);
  const { min } = xAxis.getExtremes();
  const startBeforeAxis = !navigator && group.startDate <= min;

  if (!startBeforeAxis) {
    xAxis.removePlotLine(groupStartPlotLineId(group));
  }
  if (group.endedByNull) {
    xAxis.removePlotLine(groupEndPlotLineId(group));
  }
  if (group.nextStartDate !== null) {
    xAxis.removePlotLine(groupEndPlotBandId(group));
  }
}

function removeGroupBeforeAxisLabel(chart: Highcharts.Chart) {
  const groupBeforeAxisLabel = document.getElementById(startBeforeAxisLabelId(chart));
  if (groupBeforeAxisLabel) {
    chart.container.removeChild(groupBeforeAxisLabel);
  }
}

function renderVisibleGroups(
  chart: Highcharts.Chart,
  groups: Group[],
  period: Period,
  setPeriod: (p: Period) => void,
) {
  const visibleGroups = groups.filter(c => intersectPeriod(c, period));

  if (visibleGroups.length < MAX_GROUPS_IN_PERIOD) {
    for (const group of visibleGroups) renderGroup(chart, group, setPeriod, false);
  }

  return visibleGroups;
}

function useAnalysisGroupPlotLines({
  chartRef,
  groups,
  period,
  setPeriod,
}: {
  chartRef: React.RefObject<Highcharts.Chart>;
  groups: Group[];
  period: Period;
  setPeriod: (p: Period) => void;
}) {
  const componentWillUnmount = React.useRef(false);

  // This is componentWillUnmount
  React.useLayoutEffect(
    () => () => {
      componentWillUnmount.current = true;
    },
    [],
  );

  React.useLayoutEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      throw new Error("chart not found");
    }

    if (!groups.length) return;

    if (groups.length < MAX_GROUPS) {
      for (const group of groups) renderGroup(chart, group, setPeriod, true);
    }

    return () => {
      if (componentWillUnmount.current) return;
      for (const group of groups) destroyGroup(chart, group, true);
    };
  }, [chartRef, groups, setPeriod]);

  const chartContainerRef = React.useRef<HTMLElement | null>(null);
  const visibleGroups = React.useRef<Group[]>([]);

  React.useLayoutEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      throw new Error("chart not found");
    }
    chartContainerRef.current = chart.container;
    if (!groups.length) return;

    visibleGroups.current = renderVisibleGroups(chart, groups, period, setPeriod);

    return () => {
      if (componentWillUnmount.current) return;
      removeGroupBeforeAxisLabel(chart);
      visibleGroups.current.map(group => destroyGroup(chart, group, false));
    };
  }, [chartRef, groups, period, setPeriod]);

  useObserverCallback(
    () => {
      const chart = chartRef.current;
      if (!chart) {
        // eslint-disable-next-line no-console
        console.warn("chart not found");
        return;
      }
      removeGroupBeforeAxisLabel(chart);
      for (const group of visibleGroups.current) destroyGroup(chart, group, false);

      visibleGroups.current = renderVisibleGroups(chart, groups, period, setPeriod);
    },
    [chartRef, groups, period, setPeriod],
    chartContainerRef,
    { fireOnInit: false, debounced: true },
  );
}

export default useAnalysisGroupPlotLines;
